<!--
  SchemaSelect.vue

  A select dropdown for choosing schemas with optional type filtering.
  Uses cache-first fetching to avoid redundant network calls when used in tables.

  PROPS:
    - modelValue: Number|null - The selected schema ID (v-model)
    - recordType: String|null - Filter schemas by record type (excludes type's base schema)
    - allowNone: Boolean - Whether to show a "None" option (default: true)
    - noneLabel: String - Label for the "None" option / shown when modelValue is null (default: "None")
    - placeholder: String - Shown when allowNone is false and nothing is selected (default: "Select schema...")
    - size: String - Size class (small, normal, medium, large)

  EVENTS:
    - @update:modelValue - Emitted when selection changes

  USAGE:
    All schemas grouped by type:
      <SchemaSelect v-model="schemaId" />

    Schemas for a specific type (excludes type schema):
      <SchemaSelect v-model="schemaId" record-type="person" />

    Without "None" option:
      <SchemaSelect v-model="schemaId" :allow-none="false" />

    In a table cell:
      <SchemaSelect v-model="record.schema_id" :record-type="record.type" size="small" />
-->
<template>
  <div class="control schema-select">
    <div class="select" :class="sizeClass">
      <select
        :value="modelValue ?? ''"
        :disabled="readonly"
        :class="{ 'has-placeholder': !modelValue }"
        @change="handleChange"
      >
        <option v-if="allowNone" value="">{{ noneLabel }}</option>
        <option v-else-if="!modelValue" value="" disabled>{{ placeholder }}</option>

        <!-- Untyped-only display (e.g. listing schemas) -->
        <template v-if="untypedOnly">
          <option v-for="schema in untypedSchemas" :key="schema.id" :value="schema.id">
            {{ schema.name }}
          </option>
        </template>

        <!-- Grouped display (no recordType filter) -->
        <template v-else-if="!recordType && groupedSchemas.length > 0">
          <template v-for="group in groupedSchemas" :key="group.type">
            <optgroup :label="group.label">
              <option v-for="schema in group.schemas" :key="schema.id" :value="schema.id">
                {{ schema.name }}
              </option>
            </optgroup>
          </template>
        </template>

        <!-- Flat display (with recordType filter) -->
        <template v-else-if="recordType && filteredSchemas.length > 0">
          <option v-for="schema in filteredSchemas" :key="schema.id" :value="schema.id">
            {{ schema.name }}
          </option>
        </template>

        <!-- Empty state -->
        <option v-if="schemas.length === 0" value="" disabled>No schemas available</option>
      </select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { storeToRefs } from "pinia"
import type { Schema } from "@auger/core"
import { useSchemasStore } from "@/stores/schemas"
import { useTypesStore } from "@/stores/types"

const props = withDefaults(
  defineProps<{
    modelValue?: number | null
    recordType?: string | null
    // Show only untyped schemas (record_type === null), e.g. listing schemas.
    // Takes precedence over recordType grouping/filtering.
    untypedOnly?: boolean
    readonly?: boolean
    placeholder?: string
    allowNone?: boolean
    noneLabel?: string
    size?: "small" | "normal" | "medium" | "large" | null
  }>(),
  {
    modelValue: null,
    recordType: null,
    untypedOnly: false,
    readonly: false,
    placeholder: "Select schema...",
    allowNone: true,
    noneLabel: "None",
    size: null,
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: number | null]
}>()

const typesStore = useTypesStore()
const { schemas } = storeToRefs(useSchemasStore())

const sizeClass = computed(() => {
  if (!props.size) return null
  return `is-${props.size}`
})

// Get type schema ID for the current record type
const typeSchemaId = computed(() => {
  if (!props.recordType) return null
  const type = typesStore.get(props.recordType)
  return type?.schema_id || null
})

// Filter schemas when recordType is provided: only schemas for that type,
// excluding the type's base schema (its fields are inherited automatically).
const filteredSchemas = computed(() => {
  if (!props.recordType) return schemas.value

  return schemas.value.filter(
    (schema) => schema.record_type === props.recordType && schema.id !== typeSchemaId.value
  )
})

// Untyped schemas (no record_type) — used for listing schemas.
const untypedSchemas = computed(() => schemas.value.filter((schema) => !schema.record_type))

interface SchemaGroup {
  type: string
  label: string
  schemas: Schema[]
}

const groupedSchemas = computed<SchemaGroup[]>(() => {
  if (props.recordType) return []

  const groups = new Map<string, Schema[]>()

  schemas.value.forEach((schema: Schema) => {
    const type = schema.record_type || "untyped"
    if (!groups.has(type)) {
      groups.set(type, [])
    }
    groups.get(type)!.push(schema)
  })

  const result: SchemaGroup[] = []

  typesStore.types.forEach((type) => {
    const typeSchemas = groups.get(type.name)
    if (typeSchemas && typeSchemas.length > 0) {
      result.push({
        type: type.name,
        label: type.plural_label ?? type.name,
        schemas: typeSchemas,
      })
    }
  })

  const untypedSchemas = groups.get("untyped")
  if (untypedSchemas && untypedSchemas.length > 0) {
    result.push({
      type: "untyped",
      label: "Untyped Schemas",
      schemas: untypedSchemas,
    })
  }

  return result
})

const handleChange = (event: Event) => {
  const value = (event.target as HTMLSelectElement).value
  emit("update:modelValue", value === "" || value === "null" ? null : Number(value))
}
</script>

<style>
.schema-select .select {
  width: 100%;
}

.schema-select select {
  width: 100%;
}

.schema-select select.has-placeholder {
  color: var(--auger-text-weak);
}
</style>
